import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Icon } from '../utils/icons';
import { useStaffRole } from '../hooks/useStaffRole';
import { NO_BORROWER_DATA } from '../constants/staffRoleAccess';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const TYPE_CONFIG = {
  borrower_registered: { icon: 'user',         color: '#3a6ad9', bg: '#edf1fb', label: 'New Registration' },
  status_changed:      { icon: 'arrow',        color: '#3a6ad9', bg: '#edf1fb', label: 'Status Changed' },
  document_uploaded:   { icon: 'document',     color: '#3a6ad9', bg: '#edf1fb', label: 'Document Uploaded' },
  borrower_message:    { icon: 'chat',         color: '#3a6ad9', bg: '#edf1fb', label: 'Borrower Message' },
  showing_request:     { icon: 'home',         color: '#3a6ad9', bg: '#edf1fb', label: 'Showing Request' },
  offer_request:       { icon: 'document',     color: '#ff082f', bg: '#fff0f3', label: 'Offer Request' },
  partner_referral:    { icon: 'users',        color: '#f5b832', bg: '#fffbef', label: 'New Buyer' },
  partner_message:     { icon: 'chat',         color: '#f5b832', bg: '#fffbef', label: 'Partner Message' },
  partner_registered:  { icon: 'users',        color: '#3a6ad9', bg: '#edf1fb', label: 'New Partner' },
  // 🔑 ITS OWN CHIP, because it is its own event. A firm arriving brings a payee,
  // a W-9 and a counter-signer; a person joining an existing firm brings none of
  // those, and both used to render as "New Partner".
  account_registered:  { icon: 'building',     color: '#3a6ad9', bg: '#edf1fb', label: 'New Account' },
  // ⚠️ THIS TYPE HAD NO ENTRY AT ALL and fell through to the generic chip reading
  // "Alert" — a payout-banking change, which is the one partner event a rep most
  // needs to recognise at a glance.
  partner_banking_changed: { icon: 'currency', color: '#b45309', bg: '#fffbeb', label: 'Banking Changed' },
  partner_active:      { icon: 'check-circle', color: '#2d6a0a', bg: '#f4fee8', label: 'Partner Active' },
};
const DEFAULT_CFG = { icon: 'bell', color: '#6b7280', bg: '#f3f4f6', label: 'Alert' };

function timeAgo(str) {
  if (!str) return '';
  const diff = Date.now() - new Date(str).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(diff / 3600000);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(diff / 86400000);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function AdminAlertsPanel({ category, title, subtitle }) {
  const navigate = useNavigate();
  const token = localStorage.getItem('doorly_admin_token');
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  // 🚨 A ROLE WITHOUT BORROWER DATA READS AND MARKS ITS OWN PARTNER ALERTS ONLY
  // (utils/partnerScope.staffAlertScope); DELETE stays refused for it, because
  // the alert table is shared by every role. So Dismiss and Clear all are not
  // offered — a button that 403s is the dead affordance staffNav.js removes.
  const { role } = useStaffRole();
  const canDelete = !NO_BORROWER_DATA.includes(role);

  const fetchAlerts = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category && category !== 'all') params.set('category', category);
      if (filter === 'unread') params.set('status', 'unread');
      else if (filter === 'read') params.set('status', 'read');
      if (search) params.set('search', search);
      params.set('limit', '100');
      const res = await axios.get(`${API}/api/admin/alerts?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setAlerts(res.data.alerts || []);
    } catch {}
    setLoading(false);
  }, [token, category, filter, search]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const markRead = async (id) => {
    setAlerts(prev => prev.map(a => a.id === id ? { ...a, is_read: true } : a));
    try { await axios.put(`${API}/api/admin/alerts/${id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } }); } catch {}
  };

  const markAllRead = async () => {
    setAlerts(prev => prev.map(a => ({ ...a, is_read: true })));
    const params = category && category !== 'all' ? `?category=${category}` : '';
    try { await axios.put(`${API}/api/admin/alerts/mark-all-read${params}`, {}, { headers: { Authorization: `Bearer ${token}` } }); } catch {}
  };

  const dismiss = async (id, e) => {
    e.stopPropagation();
    setAlerts(prev => prev.filter(a => a.id !== id));
    try { await axios.delete(`${API}/api/admin/alerts/${id}`, { headers: { Authorization: `Bearer ${token}` } }); } catch {}
  };

  const clearAll = async () => {
    if (!window.confirm('Clear all alerts?')) return;
    setAlerts([]);
    const params = category && category !== 'all' ? `?category=${category}` : '';
    try { await axios.delete(`${API}/api/admin/alerts${params}`, { headers: { Authorization: `Bearer ${token}` } }); } catch {}
  };

  const unreadCount = alerts.filter(a => !a.is_read).length;
  const filtered = filter === 'unread' ? alerts.filter(a => !a.is_read) : alerts;

  return (
    <AdminLayout>
      <div style={{ padding: '28px 32px', maxWidth: '900px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ margin: '0 0 4px', fontSize: '22px', fontWeight: '800', color: '#111827' }}>{title || 'Alerts'}</h1>
            <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
              {subtitle || 'Activity alerts'} · {unreadCount > 0 ? `${unreadCount} unread` : 'all caught up'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {unreadCount > 0 && (
              <button onClick={markAllRead} style={{ padding: '7px 14px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#374151', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <Icon name="check" size={13} color="#374151" /> Mark all read
              </button>
            )}
            {canDelete && alerts.length > 0 && (
              <button onClick={clearAll} style={{ padding: '7px 14px', background: '#fff0f3', border: '1px solid #ffc5ca', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#ff082f' }}>Clear all</button>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input type="text" placeholder="Search alerts..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ height: '36px', padding: '0 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none', width: '220px' }} />
          <div style={{ display: 'flex', gap: '0', background: '#f3f4f6', borderRadius: '10px', padding: '3px' }}>
            {['all', 'unread', 'read'].map(tab => (
              <button key={tab} onClick={() => setFilter(tab)}
                style={{ padding: '6px 14px', background: filter === tab ? '#fff' : 'transparent', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: filter === tab ? '700' : '500', color: filter === tab ? '#111827' : '#6b7280', boxShadow: filter === tab ? '0 1px 3px rgba(0,0,0,0.06)' : 'none', textTransform: 'capitalize' }}>
                {tab}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '60px', textAlign: 'center' }}>
            <div style={{ marginBottom: '14px' }}><Icon name="bell" size={40} color="#d1d5db" /></div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#374151', marginBottom: '6px' }}>
              {filter === 'unread' ? 'No unread alerts' : 'No alerts yet'}
            </div>
            <div style={{ fontSize: '13px', color: '#9ca3af' }}>Alerts will appear here when activity is triggered.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {filtered.map(alert => {
              const cfg = TYPE_CONFIG[alert.type] || DEFAULT_CFG;
              return (
                <div key={alert.id} onClick={() => { if (!alert.is_read) markRead(alert.id); if (alert.link) navigate(alert.link); }}
                  style={{ background: alert.is_read ? '#fff' : '#fafcff', border: `1px solid ${alert.is_read ? '#e5e7eb' : '#c5d3f5'}`, borderLeft: `4px solid ${alert.is_read ? '#e5e7eb' : cfg.color}`, borderRadius: '12px', padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: '14px', cursor: alert.link ? 'pointer' : 'default', transition: 'background 0.12s' }}
                  onMouseEnter={e => { if (alert.link) e.currentTarget.style.background = '#f9fafb'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = alert.is_read ? '#fff' : '#fafcff'; }}>
                  <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon name={cfg.icon} size={18} color={cfg.color} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '3px' }}>
                      <div style={{ fontSize: '14px', fontWeight: alert.is_read ? '600' : '700', color: '#111827', lineHeight: '1.4' }}>{alert.title}</div>
                      {!alert.is_read && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3a6ad9', flexShrink: 0, marginTop: '6px' }} />}
                    </div>
                    {alert.description && <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.5', marginBottom: '6px' }}>{alert.description}</div>}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '11px', color: '#9ca3af' }}>{timeAgo(alert.created_at)}</span>
                      <span style={{ fontSize: '10px', fontWeight: '700', padding: '1px 7px', borderRadius: '6px', background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                      {alert.link && <span style={{ fontSize: '11px', color: '#3a6ad9', fontWeight: '600' }}>View →</span>}
                    </div>
                  </div>
                  {canDelete && <button onClick={(e) => dismiss(alert.id, e)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', fontSize: '18px', lineHeight: 1, padding: '2px', flexShrink: 0 }} title="Dismiss">×</button>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
